import { invariant } from "./invariant.js";

/**
 * Parse a dollar string or number into integer cents.
 * Handles "$49.95", "49.95", "$1,234.56", 49.95, etc.
 *
 * Rounds half-up away from zero at the third decimal digit:
 *   toCents("1.005")  === 101
 *   toCents("-0.005") === -1
 *
 * Implementation parses the decimal string digit-by-digit instead of
 * multiplying a float by 100. This avoids IEEE-754 drift like
 * `1.005 * 100 === 100.49999999999999` which would silently lose a cent.
 */
export function toCents(value: string | number): number {
	let str: string;
	if (typeof value === "string") {
		str = value.replace(/[$,\s]/g, "");
		invariant(str !== "", "Cannot convert empty string to cents");
	} else {
		invariant(!Number.isNaN(value), "Cannot convert NaN to cents");
		invariant(Number.isFinite(value), `Cannot convert non-finite number ${value} to cents`);
		// String(n) returns the shortest decimal that round-trips back to the
		// same float, so `String(1.005) === "1.005"` and `String(19.99) === "19.99"`.
		// We then parse decimal digits directly — no float arithmetic.
		str = String(value);
	}

	const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(str);
	invariant(match, `Cannot convert "${value}" to cents`);

	const sign = match[1];
	const whole = match[2] ?? "";
	const frac = match[3] ?? "";

	let cents = Number(whole) * 100;
	if (frac.length >= 1) cents += Number(frac[0]) * 10;
	if (frac.length >= 2) cents += Number(frac[1]);
	if (frac.length >= 3 && Number(frac[2]) >= 5) cents += 1;

	return sign === "-" ? -cents : cents;
}

/**
 * Convert integer cents to a dollar number for display purposes only.
 * Do NOT use the result for further arithmetic — stay in cents.
 */
export function fromCents(cents: number): number {
	assertCents(cents);
	return cents / 100;
}

/**
 * Sum any number of integer cent amounts. No floating point involved.
 */
export function addCents(...amounts: number[]): number {
	let total = 0;
	for (const amount of amounts) {
		assertCents(amount);
		total += amount;
	}
	return total;
}

/**
 * Subtract b from a in integer cents. Negative results are allowed (credits /
 * discounts). Callers that treat a negative subtotal as a logic error should
 * `invariant(result >= 0, ...)` themselves.
 */
export function subtractCents(a: number, b: number): number {
	assertCents(a);
	assertCents(b);
	return a - b;
}

/**
 * Multiply cents by a quantity. Round half-up to nearest cent after multiplication.
 * Use for: baseFee * numberOfPayrolls, perEeFee * employeeCount, etc.
 *
 * All current callers pass positive cents and positive quantities. If a future
 * caller passes a negative, review the rounding convention — `roundHalfUp`
 * rounds half-up toward positive infinity (`-0.5 → 0`), not away from zero.
 */
export function multiplyCents(cents: number, quantity: number): number {
	assertCents(cents);
	return roundHalfUp(cents * quantity);
}

/**
 * Round a number to the nearest integer using half-up toward positive infinity:
 *   0.5  →  1
 *  -0.5  →  0
 *  -1.5  → -1
 *
 * NOTE: This is NOT the "half-up away from zero" convention used by `toCents`.
 * The two diverge on negative halves. `multiplyCents` is the only internal
 * caller today and is always called with positive operands, so the divergence
 * is dormant.
 */
export function roundHalfUp(value: number): number {
	return Math.floor(value + 0.5);
}

/**
 * Format integer cents as a dollar string: 4995 -> "$49.95"
 */
export function formatDollars(cents: number): string {
	assertCents(cents);
	const negative = cents < 0;
	const absCents = Math.abs(cents);
	const dollars = Math.floor(absCents / 100);
	const remainder = absCents % 100;
	const formatted = `$${dollars.toLocaleString("en-US")}.${String(remainder).padStart(2, "0")}`;
	return negative ? `-${formatted}` : formatted;
}

/**
 * Assert that a value is a valid integer cent amount.
 */
function assertCents(value: number): void {
	invariant(Number.isInteger(value), `Expected integer cents, got ${value}`);
}
